import { TruncatedHeaderCell } from 'modules/messagingInsights/components/TruncatedHeaderCell';
import { mapRegions, timezoneOptions } from 'modules/messagingInsights/constants';
import { ColumnData } from 'modules/messagingInsights/types';
import { formatNumberGivenValueType } from 'modules/productAnalytics/utils';
import moment from 'moment';
import {
  DraftBadge,
  FinishedBadge,
  ReadyBadge,
  RecalledBadge,
  RecurringBadge,
  RunningBadge,
  ScheduledBadge,
} from 'ui/badge';
import { NUMBER, PERCENTAGE } from 'ui/card/constants';
import { RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { Skeleton } from 'ui/skeleton';
import { colors } from 'ui/theme';
import { guessCurrentTimeZone } from 'utils/timeZone';

import {
  Cell,
  CenterText,
  LoadingTableHeaderCell,
  RightAlignText,
  StyledHeatmapThumbnail,
  TabularNumberFont,
} from './components/Styled';

export const CAMPAIGN_ANALYTICS = 'CampaignAnalytics';

export const DRAFT = 'Draft';
export const SCHEDULED = 'Scheduled';
export const READY = 'Ready';
export const RUNNING = 'Running';
export const FINISHED = 'Finished';

export const DEVICE_DATA = 'Device Data';

export const PRE_READY_STATES = [DRAFT];

export const READY_STATES = [READY, SCHEDULED];

export const PRE_ACTIVATION_STATES = [...READY_STATES, DRAFT];

export const POST_ACTIVATION_STATES = [RUNNING, FINISHED];

export const CAMPAIGN_REVENUE = 'Revenue';

export const GREEN_SHADES = ['#4AB99D', '#7CD0BB', '#0E9976', '#C3EFE3', '#3BB193'];
export const YELLOW_SHADES = ['#E0BB16', '#F5D54A', '#997D00', '#FCECA9', '#CEAB10'];

export const InAppToBreakdown = {
  'Total In-app Opens': 'Total In-app Opens Breakdown',
  'Unique In-app Opens': 'Unique In-app Opens Breakdown',
  'Total In-app Clicks': 'Total In-app Clicks Breakdown',
  'Unique In-app Clicks': 'Unique In-app Clicks Breakdown',
  'In-app Click Rate': 'In-App Click Rate Breakdown',
  'Total In-apps Delivered': 'Total In-apps Delivered Breakdown',
  'Unique In-apps Delivered': 'Unique In-apps Delivered Breakdown',
  'Total In-app Closes': 'Total In-app Closes Breakdown',
  'Total In-app Deletes': 'Total In-app Deletes Breakdown',
  'Total Inbox Impressions': 'Total Inbox Impressions Breakdown',
};

export const InAppMetricsToBreakdownArr: Record<string, string>[] = [
  { name: 'TotalInAppOpensBreakdown' },
  // { name: 'UniqueInAppOpensBreakdown' },
  { name: 'TotalInAppClicksBreakdown' },
  // { name: 'UniqueInAppClicksBreakdown' },
  { name: 'InAppClickRateBreakdown' },
  { name: 'TotalInAppsDeliveredBreakdown' },
  // { name: 'UniqueInAppsDeliveredBreakdown' },
  { name: 'TotalInAppClosesBreakdown' },
  { name: 'TotalInAppDeletesBreakdown' },
  { name: 'TotalInboxImpressionsBreakdown' },
];

export const ConversionMetricOptions = [
  {
    categoryId: 'conversions',
    label: 'Total Custom Conversions',
    id: 'TotalCustomConversion',
    value: 'TotalCustomConversion',
  },
  {
    categoryId: 'conversions',
    label: 'Unique Custom Conversions',
    id: 'UniqueCustomConversion',
    value: 'UniqueCustomConversion',
  },
  {
    categoryId: 'conversions',
    label: 'Sum of Custom Conversions',
    id: 'SumCustomConversion',
    value: 'SumCustomConversion',
  },
  {
    categoryId: 'conversions',
    label: 'Average Custom Conversion Value',
    id: 'AverageCustomConversionValue',
    value: 'AverageCustomConversionValue',
  },
];

export const badgeObj = {
  Draft: <DraftBadge />,
  Ready: <ReadyBadge />,
  Scheduled: <ScheduledBadge />,
  Recurring: <RecurringBadge />,
  Running: <RunningBadge />,
  Finished: <FinishedBadge />,
  Recalled: <RecalledBadge />,
};

export const BASE_TIME_SERIES_COLUMN: ColumnData = {
  name: 'Date / time',
  value: 'dateTime',
  align: 'flex-start',
  RowCell: ({ row }) => {
    const formattedDate = moment(row.dateTime).format('MM/DD/YYYY h:mma');
    return (
      <Cell>
        <CenterText>
          <TruncatedHeaderCell id={formattedDate} name={formattedDate} textOnly />
        </CenterText>
      </Cell>
    );
  },
};

export const EmailListName: ColumnData = {
  name: 'Email List Name',
  value: 'EmailListName',
  align: 'flex-start',
  RowCell: ({ row }) => (
    <Cell>
      <CenterText>{row.EmailListName}</CenterText>
    </Cell>
  ),
};

export const DELIVERY_OVERVIEW = {
  Email: [
    { name: 'EmailDeliveryRate' },
    { name: 'TotalEmailSend' },
    { name: 'TotalEmailDelivered' },
    { name: 'EmailBounceRate' },
    { name: 'TotalEmailBounce' },
    { name: 'TotalEmailSendSkip' },
  ],
  Push: [
    { name: 'PushDeliveryRate' },
    { name: 'TotalPushSend' },
    { name: 'PushBounceRate' },
    { name: 'UniquePushBounce' },
    { name: 'TotalPushSendSkip' },
  ],
  SMS: [
    { name: 'SMSDeliveryRate' },
    { name: 'TotalSMSSend' },
    { name: 'TotalSMSDelivered' },
    { name: 'SMSBounceRate' },
    { name: 'TotalSMSBounce' },
    { name: 'TotalSMSSendSkip' },
  ],
  WebPush: [{ name: 'TotalWebPushSend' }, { name: 'UniqueWebPushSend' }],
  InApp: [
    { name: 'TotalInAppDelivery' },
    { name: 'UniqueInAppDelivered' },
    { name: 'TotalInAppSend' },
    { name: 'UniqueInAppSend' },
    { name: 'TotalInboxImpression' },
  ],
  Embedded: [{ name: 'TotalEmbeddedRetrieved' }, { name: 'UniqueEmbeddedRetrieved' }],
};

export const DELIVERY_OVERVIEW_NICENAME = {
  Email: [
    'Email Delivery Rate',
    'Total Email Sends',
    'Total Emails Delivered',
    'Email Bounce Rate',
    'Total Emails Bounced',
    'Total Email Send Skips',
  ],
  Push: [
    'Push Delivery Rate',
    'Total Pushes Sent',
    'Push Bounce Rate',
    'Unique Pushes Bounced',
    'Total Push Send Skips',
  ],
  SMS: [
    'SMS Delivery Rate',
    'Total SMS Sent',
    'Total SMS Delivered',
    'SMS Bounce Rate',
    'Total SMS Bounced',
    'Total SMS Send Skips',
  ],
  WebPush: ['Total Web Pushes Sent', 'Unique Web Pushes Sent'],
  InApp: [
    'Total In-apps Delivered',
    'Unique In-apps Delivered',
    'Total In-app Sent',
    'Unique In-app Sends',
    'Total Inbox Impressions',
  ],
  Embedded: ['Total Embedded Retrieved', 'Unique Embedded Retrieved'],
};

export const DELIVERY_SECTION_TO_GRAPH_METRIC = {
  Email: {
    Delivery: 'Email Delivery Rate',
    Bounces: 'Email Bounce Rate',
    'Send skips': 'Total Email Send Skips',
  },
  Push: {
    Delivery: 'Push Delivery Rate',
    Bounces: 'Push Bounce Rate',
    'Send skips': 'Total Push Send Skips',
  },
  SMS: {
    Delivery: 'SMS Delivery Rate',
    Bounces: 'SMS Bounce Rate',
    'Send skips': 'Total SMS Send Skips',
  },
  WhatsApp: {
    Delivery: 'WhatsApp Delivery Rate',
    Bounces: 'WhatsApp Bounce Rate',
    'Send skips': 'Total WhatsApp Send Skips',
  },
};

export const DELIVERY_GRAPH_NICENAME = (isInAppRecallUIEnabled: boolean) => ({
  Email: [
    {
      metric: 'Email Delivery Rate',
      label: 'Email delivery rate',
      isRate: true,
      color: colors.cyan80,
    },
    {
      metric: 'Email Bounce Rate',
      label: 'Email bounce rate',
      isRate: true,
      color: colors.purple90,
      buckets: [
        {
          metric: 'HARD_BOUNCE',
          label: 'Hard bounces',
          isRate: true,
          color: colors.purple90,
        },
        {
          metric: 'SOFT_BOUNCE',
          label: 'Soft bounces',
          isRate: true,
          color: colors.purple50,
        },
      ],
    },
    {
      metric: 'Total Email Send Skips',
      label: 'Total email send skips',
      isRate: false,
      dividendMetric: 'Total Emails Delivered',
      color: colors.green90,
    },
  ],
  Push: [
    {
      metric: 'Push Delivery Rate',
      label: 'Push delivery rate',
      isRate: true,
      color: colors.cyan80,
    },
    { metric: 'Push Bounce Rate', label: 'Push bounce rate', isRate: true, color: colors.green100 },
    {
      metric: 'Total Push Send Skips',
      label: 'Total push send skips',
      isRate: false,
      dividendMetric: 'Total Pushes Delivered',
      color: colors.purple90,
    },
  ],
  SMS: [
    { metric: 'SMS Delivery Rate', label: 'SMS delivery rate', isRate: true, color: colors.cyan80 },
    { metric: 'SMS Bounce Rate', label: 'SMS bounce rate', isRate: true, color: colors.green100 },
    {
      metric: 'Total SMS Send Skips',
      label: 'Total SMS send skips',
      isRate: false,
      dividendMetric: 'Total SMS Delivered',
      color: colors.purple90,
    },
  ],
  WebPush: [
    {
      metric: 'Total Web Pushes Sent',
      label: 'Total web pushes sent',
      isRate: false,
      dividendMetric: 'Total Web Pushes Sent',
      color: colors.cyan80,
    },
    {
      metric: 'Unique Web Pushes Sent',
      label: 'Unique web pushes sent',
      isRate: false,
      dividendMetric: 'Total Web Pushes Sent',
      color: colors.green90,
    },
  ],
  InApp: [
    {
      metric: 'Total In-app Sent',
      label: 'Total In-app sent',
      isRate: false,
      dividendMetric: 'Total In-app Sent',
      color: colors.cyan80,
    },
    {
      metric: 'Total In-apps Delivered',
      label: 'Total In-apps delivered',
      isRate: false,
      dividendMetric: 'Total In-app Sent',
      color: colors.green90,
    },
    ...(isInAppRecallUIEnabled
      ? [
          {
            metric: 'Total In-app Recall',
            label: 'Total in-app recall',
            isRate: false,
            dividendMetric: 'Total In-app Sent',
            color: colors.red60,
          },
        ]
      : []),
    {
      metric: 'Total Inbox Impressions',
      label: 'Total inbox impressions',
      isRate: false,
      dividendMetric: 'Total In-apps Delivered',
      color: colors.purple90,
    },
  ],
  Embedded: [],
  WhatsApp: [
    {
      metric: 'WhatsApp Delivery Rate',
      label: 'WhatsApp delivery rate',
      isRate: true,
      color: colors.cyan80,
    },
    {
      metric: 'WhatsApp Bounce Rate',
      label: 'WhatsApp bounce rate',
      isRate: true,
      color: colors.purple100,
      buckets: [
        {
          metric: 'HARD_BOUNCE',
          label: 'Hard bounces',
          isRate: true,
          color: colors.purple100,
        },
        {
          metric: 'SOFT_BOUNCE',
          label: 'Soft bounces',
          isRate: true,
          color: colors.purple60,
        },
      ],
    },
    {
      metric: 'Total WhatsApp Send Skips',
      label: 'Total WhatsApp send skips',
      isRate: false,
      dividendMetric: 'Total WhatsApp Delivered',
      color: colors.green90,
    },
  ],
});

export const DELIVERY_LAYOUT = (isInAppRecallUIEnabled: boolean) => ({
  Email: {
    left: [
      { header: 'Sends', values: ['Total Email Sends', 'Unique Email Sends'] },
      {
        header: 'Bounces',
        values: ['Email Bounce Rate', 'Unique Emails Bounced', 'Total Emails Bounced'],
      },
    ],
    right: [
      {
        header: 'Delivery',
        values: ['Email Delivery Rate', 'Total Emails Delivered', 'Unique Emails Delivered'],
      },
    ],
  },
  Push: {
    left: [
      { header: 'Sends', values: ['Total Pushes Sent', 'Unique Pushes Sent'] },
      {
        header: 'Bounces',
        values: ['Push Bounce Rate', 'Total Pushes Bounced', 'Unique Pushes Bounced'],
      },
    ],
    right: [
      {
        header: 'Delivery',
        values: ['Push Delivery Rate', 'Total Pushes Delivered', 'Unique Pushes Delivered'],
      },
    ],
  },
  SMS: {
    left: [
      { header: 'Sends', values: ['Total SMS Sent', 'Unique SMS Sent'] },
      {
        header: 'Bounces',
        values: ['SMS Bounce Rate', 'Total SMS Bounced', 'Unique SMS Bounced'],
      },
    ],
    right: [
      {
        header: 'Delivery',
        values: ['SMS Delivery Rate', 'Total SMS Delivered', 'Unique SMS Delivered'],
      },
    ],
  },
  InApp: {
    left: [{ header: 'Sends', values: ['Total In-app Sent', 'Unique In-app Sends'] }].concat(
      isInAppRecallUIEnabled ? { header: 'Recall', values: ['Total In-app Recall'] } : [],
    ),
    right: [
      {
        header: 'Delivery',
        values: ['Total In-apps Delivered', 'Unique In-apps Delivered'],
      },
    ],
  },
  WebPush: {
    left: [],
    right: [],
  },
  Embedded: {
    left: [
      {
        header: 'Retrieved',
        values: ['Total Embedded Retrieved', 'Unique Embedded Retrieved'],
      },
    ],
    right: [],
  },
  WhatsApp: {
    left: [
      { header: 'Sends', values: ['Total WhatsApp Sends', 'Unique WhatsApp Sends'] },
      {
        header: 'Bounces',
        values: ['WhatsApp Bounce Rate', 'Unique WhatsApp Bounced', 'Total WhatsApp Bounced'],
      },
    ],
    right: [
      {
        header: 'Delivery',
        values: ['WhatsApp Delivery Rate', 'Total WhatsApp Delivered', 'Unique WhatsApp Delivered'],
      },
    ],
  },
});

export const ENGAGEMENT_OVERVIEW = {
  Email: [
    { name: 'ConversionPerEmailDeliveredRate' },
    { name: 'TotalEmailDelivered' },
    { name: 'TotalEmailOpen' },
    { name: 'TotalEmailHoldOut' },
    { name: 'TotalEmailClick' },
    { name: 'TotalPurchase' },
    { name: 'Revenue' },
  ],
  Push: [
    { name: 'ConversionPerPushDeliveredRate' },
    { name: 'TotalPushDelivered' },
    { name: 'TotalPushOpen' },
    { name: 'TotalPurchase' },
    { name: 'Revenue' },
  ],
  SMS: [
    { name: 'ConversionPerSMSDeliveredRate' },
    { name: 'TotalSMSDelivered' },
    { name: 'TotalPurchase' },
    { name: 'Revenue' },
  ],
  WebPush: [
    { name: 'ConversionPerWebPushSendRate' },
    { name: 'TotalWebPushSend' },
    { name: 'TotalWebPushClick' },
    { name: 'Revenue' },
  ],
  InApp: [
    { name: 'ConversionPerInAppOpenRate' },
    { name: 'TotalInAppSend' },
    { name: 'TotalInAppOpen' },
    { name: 'TotalInAppClick' },
    { name: 'Revenue' },
  ],
};

export const ENGAGEMENT_PURCHASE = 'Purchase';
export const ENGAGEMENT_OPENS_CLICKS = 'opensClicks';
export const ENGAGEMENT_CLOSES_DELETES = 'closesDeletes';
export const ENGAGEMENT_INBOX_IMPRESSIONS = 'inboxImpressions';

export const METRIC_CARD_DATA_LOOKUP = {
  [ENGAGEMENT_PURCHASE]: {
    Email: {
      header: 'Revenue breakdown',
      values: [
        'Revenue',
        'Purchases / M (Email)',
        'Revenue / M (Email)',
        'Total Purchases',
        'Unique Purchases',
        'Average Order Value',
      ],
    },
    Push: {
      header: 'Revenue breakdown',
      values: [
        'Revenue',
        'Purchases / M (Push)',
        'Revenue / M (Push)',
        'Total Purchases',
        'Unique Purchases',
        'Average Order Value',
      ],
    },
    SMS: {
      header: 'Revenue breakdown',
      values: [
        'Revenue',
        'Purchases / M (SMS)',
        'Revenue / M (SMS)',
        'Total Purchases',
        'Unique Purchases',
        'Average Order Value',
      ],
    },
    WebPush: {
      header: 'Revenue breakdown',
      values: ['Revenue', 'Total Purchases', 'Unique Purchases', 'Average Order Value'],
    },
    InApp: {
      header: 'Revenue breakdown',
      values: ['Revenue', 'Total Purchases', 'Unique Purchases', 'Average Order Value'],
    },
    Embedded: {
      header: 'Revenue breakdown',
      values: ['Revenue', 'Total Purchases', 'Unique Purchases', 'Average Order Value'],
    },
    WhatsApp: {
      header: 'Revenue breakdown',
      values: [
        'Revenue',
        'Purchases / M (WhatsApp)',
        'Revenue / M (WhatsApp)',
        'Total Purchases',
        'Unique Purchases',
        'Average Order Value',
      ],
    },
  },
  [ENGAGEMENT_OPENS_CLICKS]: {
    Email: {
      header: 'Opens & clicks',
      values: [
        'Email Open Rate',
        'Total Email Opens',
        'Unique Email Opens',
        'Total Emails Clicked',
        'Unique Email Clicks',
        'Email Click Rate',
        'Unique Email Opens Or Clicks',
        'Clicks / Opens',
      ],
    },
    Push: {
      header: 'Opens',
      values: ['Push Open Rate', 'Total Pushes Opened', 'Unique Pushes Opened'],
    },
    SMS: {
      header: 'Inbound',
      values: ['Total Inbound SMS', 'Unique Inbound SMS'],
    },
    WebPush: {
      header: 'Opens',
      values: ['Unique Web Push Clicks'],
    },
    InApp: {
      header: 'Opens & clicks',
      values: [
        'Total In-app Opens',
        'Unique In-app Opens',
        'Total In-app Clicks',
        'Unique In-app Clicks',
        'In-app Click Rate',
      ],
    },
    Embedded: {
      header: 'Clicks',
      values: ['Embedded Click Rate', 'Total Embedded Clicks', 'Unique Embedded Clicks'],
    },
    WhatsApp: {
      header: 'Reads & clicks',
      values: [
        'Total WhatsApp Reads',
        'Total WhatsApp Clicks',
        'Unique WhatsApp Clicks',
        'WhatsApp Click Rate',
        'Unique WhatsApp Reads Or Clicks',
      ],
    },
  },
  [ENGAGEMENT_CLOSES_DELETES]: {
    Email: {
      header: null,
      values: null,
    },
    Push: {
      header: null,
      values: null,
    },
    SMS: {
      header: null,
      values: null,
    },
    WebPush: {
      header: null,
      values: null,
    },
    InApp: {
      header: 'Closes & deletes',
      values: ['Total In-app Closes', 'Total In-app Deletes'],
    },
    Embedded: {
      header: null,
      values: null,
    },
  },
  [ENGAGEMENT_INBOX_IMPRESSIONS]: {
    Email: {
      header: null,
      values: null,
    },
    Push: {
      header: null,
      values: null,
    },
    SMS: {
      header: null,
      values: null,
    },
    WebPush: {
      header: null,
      values: null,
    },
    InApp: {
      header: 'Total inbox impressions',
      values: ['Total Inbox Impressions'],
    },
    Embedded: {
      header: null,
      values: null,
    },
  },
};

const CONVERSION_TOOLTIP = 'Total count of all conversions added to this campaign';

export const ENGAGEMENT_MULTI_CONVERSION_DEFAULT = {
  Email: [
    {
      key: 'Total Conversions',
      label: 'Conversions',
      tooltip: CONVERSION_TOOLTIP,
    },
    {
      key: 'Clicks / Opens',
      label: 'Clicks / Opens',
    },
    {
      key: 'Email Open Rate',
      label: 'Email Open Rate',
    },
  ],
  Push: [
    {
      key: 'Total Conversions',
      label: 'Conversions',
      tooltip: CONVERSION_TOOLTIP,
    },
    {
      key: 'Push Open Rate',
      label: 'Push Open Rate',
    },
    {
      key: 'Unique Pushes Opened',
      label: 'Unique Pushes Opened',
    },
  ],
  SMS: [
    {
      key: 'Total Conversions',
      label: 'Conversions',
      tooltip: CONVERSION_TOOLTIP,
    },
    {
      key: 'Total Inbound SMS',
      label: 'Total Inbound SMS',
    },
  ],
  WebPush: [
    {
      key: 'Total Conversions',
      label: 'Conversions',
      tooltip: CONVERSION_TOOLTIP,
    },
    {
      key: 'Unique Web Push Clicks',
      label: 'Unique Web Push Clicks',
    },
  ],
  InApp: [
    {
      key: 'Total Conversions',
      label: 'Conversions',
      tooltip: CONVERSION_TOOLTIP,
    },
    {
      key: 'In-app Click Rate',
      label: 'In-app Click Rate',
    },
  ],
  Embedded: [
    {
      key: 'Total Conversions',
      label: 'Conversions',
      tooltip: CONVERSION_TOOLTIP,
    },
    {
      key: 'Total Embedded Clicks',
      label: 'Clicks',
    },
    {
      key: 'Embedded Click Rate',
      label: 'Embedded click rate',
    },
  ],
  WhatsApp: [
    {
      key: 'WhatsApp Read Rate',
      label: 'WhatsApp read rate',
    },
    {
      key: 'Clicks / Reads',
      label: 'Clicks / Reads',
    },
  ],
};

export const ENGAGEMENT_CONVERSION_GRAPH_WITH_CONVERSIONS = (isInAppRecallUIEnabled: boolean) => ({
  Email: [
    {
      key: 'Total Emails Delivered',
      label: 'Total emails delivered',
    },
    {
      key: 'Total Email Opens',
      label: 'Total emails opened',
    },
    {
      key: 'Total Emails Clicked',
      label: 'Total emails clicked',
    },
    {
      key: 'Conversions',
      label: 'Total conversions',
    },
  ],
  Push: [
    {
      key: 'Total Pushes Delivered',
      label: 'Total pushes delivered',
    },
    {
      key: 'Total Pushes Opened',
      label: 'Total pushes opened',
    },
    {
      key: 'Conversions',
      label: 'Total conversions',
    },
  ],
  SMS: [
    {
      key: 'Total SMS Delivered',
      label: 'Total SMS delivered',
    },
    {
      key: 'Conversions',
      label: 'Total conversions',
    },
  ],
  WebPush: [
    {
      key: 'Total Web Pushes Sent',
      label: 'Total web pushes sent',
    },
    {
      key: 'Total Web Push Clicks',
      label: 'Total web push clicks',
    },
    {
      key: 'Conversions',
      label: 'Total conversions',
    },
  ],
  InApp: [
    {
      key: 'Total In-app Sent',
      label: 'Total In-app sent',
    },
    ...(isInAppRecallUIEnabled
      ? [
          {
            key: 'Total In-app Recall',
            label: 'Total In-app Recall',
          },
        ]
      : []),
    {
      key: 'Total In-app Opens',
      label: 'Total In-app opens',
    },
    {
      key: 'Total In-app Clicks',
      label: 'Total In-app clicks',
    },
    {
      key: 'Conversions',
      label: 'Total conversions',
    },
  ],
  Embedded: [
    {
      key: 'Total Embedded Retrieved',
      label: 'Total embedded retrieved',
    },
    {
      key: 'Total Embedded Impressions',
      label: 'Total embedded impressions',
    },
    {
      key: 'Total Embedded Clicks',
      label: 'Total embedded clicks',
    },
  ],
  WhatsApp: [
    {
      key: 'Total WhatsApp Delivered',
      label: 'Total delivered',
    },
    {
      key: 'Total WhatsApp Reads',
      label: 'Total reads',
    },
    {
      key: 'Total WhatsApp Clicks',
      label: 'Total clicks',
    },
    {
      key: 'Conversions',
      label: 'Total conversions',
    },
  ],
});

export const RETENTION_OVERVIEW = {
  Email: [
    { name: 'EmailUnsubscribeRate' },
    { name: 'TotalUnsubscribe' },
    { name: 'TotalEmailSend' },
  ],
  Push: [{ name: 'PushUnsubscribeRate' }, { name: 'TotalUnsubscribe' }, { name: 'TotalPushSend' }],
  SMS: [{ name: 'TotalUnsubscribe' }, { name: 'TotalSMSSend' }],
  WebPush: [{ name: 'TotalUnsubscribe' }, { name: 'TotalWebPushSend' }],
  InApp: [{ name: 'TotalUnsubscribe' }, { name: 'TotalInAppSend' }],
  Embedded: [],
};

export const RETENTION_OVERVIEW_NICENAME = {
  Email: ['Total Unsubscribes', 'Email Unsubscribe Rate', 'Unique Unsubscribes'],
  Push: ['Total Unsubscribes', 'Push Unsubscribe Rate', 'Unique Unsubscribes'],
  SMS: ['Total Unsubscribes', 'SMS Unsubscribe Rate', 'Unique Unsubscribes'],
  WebPush: ['Total Unsubscribes', 'Unique Unsubscribes'],
  InApp: ['Total Unsubscribes', 'Unique Unsubscribes'],
  Embedded: [],
  WhatsApp: ['Total Unsubscribes', 'WhatsApp Unsubscribe Rate', 'Unique Unsubscribes'],
};

export const RETENTION_UNSUBSCRIBES_NICENAME = {
  Email: [
    'Email Unsubscribe Rate',
    'Total Unsubscribes',
    'Unique Unsubscribes',
    'Total Hosted Unsubscribe Clicks',
    'Unique Hosted Unsubscribe Clicks',
  ],
  Push: [
    'Push Unsubscribe Rate',
    'Total Unsubscribes',
    'Unique Unsubscribes',
    'Total Hosted Unsubscribe Clicks',
    'Unique Hosted Unsubscribe Clicks',
  ],
  SMS: ['SMS Unsubscribe Rate', 'Total Unsubscribes', 'Unique Unsubscribes'],
  WebPush: ['Unique Unsubscribes', 'Total Unsubscribes'],
  InApp: ['Total In-app Sent', 'Total Unsubscribes'],
  Embedded: [],
  WhatsApp: ['WhatsApp Unsubscribe Rate', 'Total Unsubscribes', 'Unique Unsubscribes'],
};

export const RETENTION_SECONDARY_NICENAME = {
  Email: { title: 'Complaints', value: ['Complaint Rate', 'Total Complaints'] },
  Push: {
    title: 'Uninstalls',
    value: ['Push Unsubscribe Rate', 'Total Unsubscribes', 'Total Pushes Sent'],
  },
  SMS: {},
  WebPush: {},
  InApp: { title: 'Deletes', value: ['Total Unsubscribes', 'Total In-app Sent'] },
  Embedded: {},
};

export const RETENTION_GRAPH_NICENAME = {
  Email: {
    graphMetric: 'Total Unsubscribes',
    graphMetricLabel: 'Total unsubscribes',
  },
  Push: {
    graphMetric: 'Total Unsubscribes',
    graphMetricLabel: 'Total unsubscribes',
  },
  SMS: {
    graphMetric: 'Total Unsubscribes',
    graphMetricLabel: 'Total unsubscribes',
  },
  WebPush: { graphMetric: 'Total Unsubscribes', graphMetricLabel: 'Total unsubscribes' },
  InApp: { graphMetric: 'Total Unsubscribes', graphMetricLabel: 'Total unsubscribes' },
  WhatsApp: { graphMetric: 'Total Unsubscribes', graphMetricLabel: 'Total unsubscribes' },
};

export const MDYTimeFormat = 'M/D/YYYY h:mm a';
export const defaultDateParams = {
  startTime: moment().subtract(7, 'd'),
  endTime: moment(),
};
export const defaultTimePreset = 'ENTIRE_CAMPAIGN';
export const defaultTimezone = guessCurrentTimeZone();

export const TIME_SERIES_AND_SEGMENTS_DEFAULT_METRICS = {
  Email: ['TotalEmailOpen', 'TotalEmailClick', 'EmailClickPerOpenRate', 'TotalUnsubscribe'],
  Push: ['TotalPushSend', 'TotalPushOpen', 'PushDeliveryRate', 'TotalUnsubscribe'],
  SMS: ['TotalSMSSend', 'SMSDeliveryRate', 'TotalUnsubscribe'],
  WebPush: ['TotalWebPushSend', 'TotalWebPushClick', 'TotalUnsubscribe'],
  InApp: ['TotalInAppSend', 'TotalInAppDelivery', 'TotalInAppOpen', 'TotalUnsubscribe'],
  Embedded: ['TotalEmbeddedRetrieved'],
  WhatsApp: ['TotalWhatsAppSend', 'WhatsAppDeliveryRate'],
};

export const loadingCols = [
  {
    name: '  ',
    value: 'loading',
    sortable: false,
    RowCell: () => (
      <Cell>
        <Skeleton />
      </Cell>
    ),
    HeaderCell: () => (
      <LoadingTableHeaderCell columnData={{ align: '' }}>
        <Skeleton />
      </LoadingTableHeaderCell>
    ),
  },
];

export const loadingRows = [
  { loading: 1 },
  { loading: 2 },
  { loading: 3 },
  { loading: 4 },
  { loading: 5 },
];

export const graphColorPalette = [
  colors.salmon50,
  colors.mustard50,
  colors.cyan50,
  colors.purple90,
  colors.green50,
  colors.cyan20,
  colors.red20,
  colors.cyan100,
  colors.purple50,
  colors.red50,
];

export const CONVERSION_METRICS_PER_MESSAGE_MEDIUM = {
  Email: ['Conversions / Unique Emails Delivered'],
  Push: ['Conversions / Unique Pushes Delivered'],
  SMS: [
    'Conversions / Unique SMS Sent',
    'Conversions / Unique SMS Sends',
    'Conversions / Unique SMS Delivered',
  ],
  WebPush: ['Conversions / Unique Web Push Sends'],
  InApp: ['Conversions / Unique In-app Opens'],
  Embedded: [],
  WhatsApp: ['Conversions / Unique WhatsApp Delivered'],
};

export const CONVERSION_RATE_METRICS_PER_MESSAGE_MEDIUM = {
  Email: 'Unique Emails Delivered',
  Push: 'Unique Pushes Delivered',
  SMS: 'Unique SMS Delivered',
  WebPush: 'Unique Web Push Sends',
  InApp: 'Unique In-app Opens',
  Embedded: 'Unique Embedded Retrived',
  WhatsApp: 'Unique WhatsApp Delivered',
};

export const HEATMAP_COLS: ColumnData[] = [
  {
    name: 'Link content',
    value: 'text',
    align: 'flex-start',
    RowCell: ({ row }) => {
      return (
        <Cell>
          <CenterText>
            {' '}
            {row.img && (
              <span>
                <StyledHeatmapThumbnail
                  src={row.img}
                  alt={`Image for link position ${row.hrefIndex + 1}`}
                />
              </span>
            )}
            <span>{row.text}</span>
          </CenterText>
        </Cell>
      );
    },
  },
  {
    name: 'URL',
    value: 'url',
    align: 'flex-start',
    RowCell: ({ row }) => {
      return (
        <Cell>
          <CenterText>{row.url ? <a href={row.url}>{row.url}</a> : '[dynamic url]'}</CenterText>
        </Cell>
      );
    },
  },
  {
    name: 'Click rate',
    value: 'clickRate',
    align: 'flex-end',
    RowCell: ({ row }) => {
      return (
        <Cell>
          <RightAlignText>
            <TabularNumberFont>
              {formatNumberGivenValueType(PERCENTAGE, row.clickRate)}
            </TabularNumberFont>
          </RightAlignText>
        </Cell>
      );
    },
  },
  {
    name: 'Total clicks',
    value: 'totalCount',
    align: 'flex-end',
    RowCell: ({ row }) => {
      return (
        <Cell>
          <RightAlignText>
            <TabularNumberFont>
              {formatNumberGivenValueType(NUMBER, row.totalCount)}
            </TabularNumberFont>
          </RightAlignText>
        </Cell>
      );
    },
  },
  {
    name: 'Unique click rate',
    value: 'uniqueClickRate',
    align: 'flex-end',
    RowCell: ({ row }) => {
      return (
        <Cell>
          <RightAlignText>
            <TabularNumberFont>
              {formatNumberGivenValueType(PERCENTAGE, row.uniqueClickRate)}
            </TabularNumberFont>
          </RightAlignText>
        </Cell>
      );
    },
  },
  {
    name: 'Unique count',
    value: 'uniqueCount',
    align: 'flex-end',
    RowCell: ({ row }) => {
      return (
        <Cell>
          <RightAlignText>
            <TabularNumberFont>
              {formatNumberGivenValueType(NUMBER, row.uniqueCount)}
            </TabularNumberFont>
          </RightAlignText>
        </Cell>
      );
    },
  },
];

export const CAMPAIGN_ANALYTICS_LOCAL_STORAGE_KEY = 'campaignAnalyticsSelectedMetrics';

export const NUMBER_SUFFIXES = ['', 'k', 'm', 'b', 't'];

export const CIRCLE = 'circle';
export const DIAMOND = 'diamond';
export const SQUARE = 'square';
export const UP_TRIANGLE = 'triangle';
export const DOWN_TRIANGLE = 'triangle-down';

// Metric Cards
export const HEIGHT_WITH_CONVERSIONS = 500;
export const HEIGHT_WITHOUT_CONVERSIONS = 250;
export const HEIGHT_OF_CONVERSION_CONTENT = 415;
export const HEIGHT_WITH_DATA_AND_GRAPH = 448;
export const HEIGHT_WITH_DATA = 403;
export const HEIGHT_WITHOUT_DATA = 250;

export const WEB_INAPP_BREAKDOWN_UNSUPPORTED_METRICS = [
  {
    mediumMetricName: 'TotalInAppSend',
    mediumMetricDisplayName: 'Total In-app Sent',
    aggregateMetricName: 'TotalSent',
    aggregateMetricDisplayName: 'Total Sent',
    tooltipContent:
      'Sends cannot be broken down by destination as there is no device information at the time of a send.',
  },
  {
    mediumMetricName: 'TotalInAppSendSkip',
    mediumMetricDisplayName: 'Total In-app Send Skips',
    aggregateMetricName: 'TotalSendSkips',
    aggregateMetricDisplayName: 'Total Send Skips',
    tooltipContent:
      'Send skips cannot be broken down by destination as there is no device information at the time of a send.',
  },
  {
    mediumMetricName: 'TotalPurchase',
    mediumMetricDisplayName: 'Total Purchases',
    aggregateMetricName: 'TotalPurchases',
    aggregateMetricDisplayName: 'Total Purchases',
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'UniqueInAppSend',
    mediumMetricDisplayName: 'Unique In-app Sends',
    aggregateMetricName: 'UniqueSent',
    aggregateMetricDisplayName: 'Unique Sent',
    tooltipContent:
      'Sends cannot be broken down by destination as there is no device information at the time of a send.',
  },
  {
    mediumMetricName: 'TotalInAppHoldout',
    mediumMetricDisplayName: 'Total In-app Holdout',
    aggregateMetricName: null,
    aggregateMetricDisplayName: null,
    tooltipContent:
      'Holdouts cannot be broken down by destination as there is no device information at the time of a holdout.',
  },
  {
    mediumMetricName: null,
    mediumMetricDisplayName: null,
    aggregateMetricName: 'TotalCustomConversions',
    aggregateMetricDisplayName: 'Total Conversions',
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'TotalCustomConversion',
    mediumMetricDisplayName: 'Total Custom Conversions',
    aggregateMetricName: null,
    aggregateMetricDisplayName: null,
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'UniqueCustomConversion',
    mediumMetricDisplayName: 'Unique Custom Conversions',
    aggregateMetricName: null,
    aggregateMetricDisplayName: null,
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'ConversionPerInAppHoldOutRate',
    mediumMetricDisplayName: 'Conversions / In-app Hold Outs',
    aggregateMetricName: 'ConversionPerHoldOutRate',
    aggregateMetricDisplayName: 'Conversions / Hold Out',
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'ConversionPerInAppOpenRate',
    mediumMetricDisplayName: 'Conversions / Unique In-app Opens', // Check if this medium - aggregate combination makes sense
    aggregateMetricName: 'ConversionPerSendRate',
    aggregateMetricDisplayName: 'Conversions / Send Delivered',
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'TotalUnsubscribe',
    mediumMetricDisplayName: 'Total Unsubscribes',
    aggregateMetricName: 'TotalUnsubscribes',
    aggregateMetricDisplayName: 'Total Unsubscribes',
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'Revenue',
    mediumMetricDisplayName: 'Revenue',
    aggregateMetricName: 'Revenue',
    aggregateMetricDisplayName: 'Revenue',
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'UniquePurchase',
    mediumMetricDisplayName: 'Unique Purchases',
    aggregateMetricName: null,
    aggregateMetricDisplayName: null,
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
  {
    mediumMetricName: 'AverageOrderValue',
    mediumMetricDisplayName: 'Average Order Value',
    aggregateMetricName: null,
    aggregateMetricDisplayName: null,
    tooltipContent: 'There is no destination information for this metric at this time.',
  },
];

export const QueryParamOptionsCA = [
  'startTime',
  'endTime',
  'timePreset',
  'timezone',
  'campaignId',
  'overviewMetrics',
  'timeSeriesMetrics',
  'segmentMetrics',
  'segment',
  'region',
  'variantId',
];

export const QueryValueOptionsByParamCA: Record<string, string[]> = {
  params: QueryParamOptionsCA,
  region: mapRegions.map((reg) => reg.value),
  timePreset: [...Object.keys(RELATIVE_DATE_PRESETS), 'ENTIRE_CAMPAIGN'],
  timezone: timezoneOptions,
};

export enum UserDataLevel {
  Dropdown = 'dropdown',
  Input = 'input',
  Restricted = 'restricted',
}
